import React, { useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import AdminSidebarProfileV2 from '@/components/v2/AdminSidebarProfileV2';
import type { SidebarItem } from '@/components/v2/types';
import { useAuth } from '@/contexts/AuthContext';

type SidebarSection = {
  title: string;
  labels: string[];
  order?: string[];
};

type SidebarFooter = {
  title: string;
  description: string;
  collapsedIcon?: React.ReactNode;
};

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

const AdminSidebarV2: React.FC<{
  items?: SidebarItem[];
  activePath?: string;
  onNavigate?: (p: string) => void;
  onCrossHostNavigate?: (item: SidebarItem) => void;
  isAdmin?: boolean;
  isCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  sections?: SidebarSection[];
  footer?: SidebarFooter;
}> = ({
  items = [],
  activePath = '/dashboard',
  onNavigate,
  onCrossHostNavigate,
  isAdmin,
  isCollapsed: externalCollapsed,
  onCollapsedChange,
  sections,
  footer,
}) => {
  const { user } = useAuth();
  const userName = user?.name || 'Admin';
  const userRole = user?.systemRole || user?.role || 'Admin';
  const userEmail = user?.email || '';
  const isUserAdmin = isAdmin || user?.role === 'admin' || user?.role === 'super_admin';
  const tenantName = (user as any)?.tenantName || (user as any)?.firmName || undefined;

  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });

  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;

  const footerTitle = footer?.title || 'Control Plane';
  const footerDescription =
    footer?.description || 'Privileged ops surface. All actions are audited.';
  const footerCollapsedIcon = footer?.collapsedIcon || 'CP';

  const setIsCollapsed = (collapsed: boolean) => {
    if (onCollapsedChange) {
      onCollapsedChange(collapsed);
    } else {
      setInternalCollapsed(collapsed);
    }
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  };

  const [isHovered, setIsHovered] = useState(false);
  const showFullContent = !isCollapsed || isHovered;

  const renderItems = (sectionItems: SidebarItem[]) => (
    <nav className="space-y-1">
      {sectionItems.map(it => {
        const active = !it.externalHref && activePath === it.path;
        const activeStyles = active
          ? {
              borderColor: 'var(--control-plane-border)',
              backgroundColor: 'var(--control-plane-active-bg)',
              color: 'var(--control-plane-text)',
            }
          : undefined;
        const itemClass = `btn relative flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-normal transition-all duration-200 border ${
          active ? '' : 'border-transparent hover:bg-slate-50 dark:hover:bg-neutral-800'
        } ${!showFullContent ? 'justify-center px-2' : ''}`;
        const iconClass = `${active ? '' : 'text-slate-800 dark:text-neutral-200'} ${!showFullContent ? 'text-lg' : ''}`;

        if (it.handoffTargetHost) {
          return (
            <button
              key={it.path}
              onClick={() => onCrossHostNavigate?.(it)}
              className={itemClass}
              title={!showFullContent ? it.label : undefined}
            >
              <span className={iconClass}>{it.icon}</span>
              {showFullContent && <span className="truncate subheading">{it.label}</span>}
            </button>
          );
        }

        if (it.externalHref) {
          return (
            <a
              key={it.path}
              href={it.externalHref}
              className={itemClass}
              title={!showFullContent ? it.label : undefined}
            >
              <span className={iconClass}>{it.icon}</span>
              {showFullContent && <span className="truncate subheading">{it.label}</span>}
            </a>
          );
        }

        return (
          <button
            key={it.path}
            aria-current={active ? 'page' : undefined}
            onClick={() => onNavigate?.(it.path)}
            className={itemClass}
            style={activeStyles}
            title={!showFullContent ? it.label : undefined}
          >
            {active && showFullContent && (
              <span
                className="absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full"
                style={{ backgroundColor: 'var(--control-plane-accent)' }}
              />
            )}
            <span className={iconClass} style={active ? { color: 'var(--control-plane-accent)' } : undefined}>
              {it.icon}
            </span>
            {showFullContent && <span className="truncate subheading">{it.label}</span>}
          </button>
        );
      })}
    </nav>
  );

  return (
    <aside
      data-sidebar
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`sidebar-legacy fixed left-0 hidden border-r border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 md:block transition-all duration-300 ease-in-out ${
        showFullContent ? 'w-[280px] shadow-sm' : 'w-[72px]'
      } ${isCollapsed && isHovered ? 'shadow-2xl z-50' : 'z-40'}`}
      style={{
        top: 'var(--internal-banner-offset, 0px)',
        height: 'calc(100vh - var(--internal-banner-offset, 0px))',
      }}
      aria-label="Sidebar"
    >
      <div className="flex h-full flex-col p-3 relative">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`absolute -right-3 top-8 z-50 h-6 w-6 rounded-full border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-500 hover:border-amber-300 dark:hover:border-amber-600 ${
            showFullContent ? '' : 'opacity-0 hover:opacity-100'
          }`}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>

        <AdminSidebarProfileV2
          name={userName}
          role={userRole}
          email={userEmail}
          tenantName={tenantName}
          isAdmin={isUserAdmin}
          isCollapsed={!showFullContent}
          onSettings={() => onNavigate?.('/settings')}
        />

        <div className="mt-3 space-y-4 overflow-y-auto">
          {Array.isArray(sections) && sections.length ? (
            sections.map(section => {
              const sectionItems = items.filter(i => section.labels.includes(i.label));
              if (!sectionItems.length) return null;
              const order = section.order && section.order.length ? section.order : section.labels;
              const orderedItems = [...sectionItems].sort((a, b) => {
                const aIdx = order.indexOf(a.label);
                const bIdx = order.indexOf(b.label);
                return (aIdx === -1 ? order.length : aIdx) - (bIdx === -1 ? order.length : bIdx);
              });

              return (
                <div key={section.title}>
                  {showFullContent && (
                    <div className="px-2 pb-1 text-[11px] font-normal uppercase tracking-normal text-slate-500 dark:text-neutral-400 subheading transition-opacity duration-300">
                      {section.title}
                    </div>
                  )}
                  {renderItems(orderedItems)}
                </div>
              );
            })
          ) : (
            <div>{renderItems(items)}</div>
          )}
        </div>

        {showFullContent ? (
          <div className="mt-4 rounded-xl border border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-800 p-3 text-xs text-slate-600 dark:text-neutral-400 transition-all duration-300">
            <div className="font-medium text-slate-700 dark:text-neutral-300">{footerTitle}</div>
            {footerDescription}
          </div>
        ) : (
          <div
            className="mt-4 rounded-xl border border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-800 p-2 flex items-center justify-center transition-all duration-300"
            title={footerTitle}
          >
            <span className="text-lg">{footerCollapsedIcon}</span>
          </div>
        )}
      </div>
    </aside>
  );
};

export default AdminSidebarV2;
